'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';

interface NavItem {
  id: string;
  label: string;
  icon: string;
  href: string;
  badge?: number;
  badgeVariant?: 'alert' | 'info' | 'warning';
}

interface NavGroup {
  id: string;
  label: string;
  items: NavItem[];
}

const navGroups: NavGroup[] = [
  {
    id: 'group-dashboards',
    label: 'Dashboards',
    items: [
      { id: 'nav-executive', label: 'Executive', icon: 'ChartBarIcon', href: '/' },
      { id: 'nav-sales', label: 'Sales', icon: 'CurrencyDollarIcon', href: '/sales-dashboard' },
      { id: 'nav-inventory', label: 'Inventory', icon: 'CubeIcon', href: '/inventory-dashboard' },
    ],
  },
  {
    id: 'group-operations',
    label: 'Operations',
    items: [
      { id: 'nav-orders', label: 'Sales Orders', icon: 'ClipboardDocumentListIcon', href: '/sales-orders', badge: 14, badgeVariant: 'info' },
      { id: 'nav-quotes', label: 'Quotations', icon: 'DocumentTextIcon', href: '/quotations', badge: 5, badgeVariant: 'warning' },
      { id: 'nav-delivery', label: 'Deliveries', icon: 'TruckIcon', href: '#', badge: 3, badgeVariant: 'alert' },
      { id: 'nav-driver', label: 'Driver App', icon: 'MapPinIcon', href: '/driver-dashboard' },
      { id: 'nav-sales-truck', label: 'Sales Trucks', icon: 'TruckIcon', href: '/sales-truck', badge: 2, badgeVariant: 'info' },
      { id: 'nav-purchase-orders', label: 'Purchase Orders', icon: 'ShoppingCartIcon', href: '/purchase-orders', badge: 2, badgeVariant: 'info' },
      { id: 'nav-warehouse', label: 'Warehouse', icon: 'BuildingStorefrontIcon', href: '#' },
    ],
  },
  {
    id: 'group-merchandising',
    label: 'Merchandising',
    items: [
      { id: 'nav-merch-dashboard', label: 'Merchandiser', icon: 'ShoppingBagIcon', href: '/merchandiser-dashboard' },
      { id: 'nav-merch-supervisor', label: 'Supervisor View', icon: 'UsersIcon', href: '/merchandiser-supervisor' },
      { id: 'nav-merch-visits', label: 'Visit Reports', icon: 'DocumentTextIcon', href: '/merchandiser-visits', badge: 3, badgeVariant: 'warning' },
    ],
  },
  {
    id: 'group-catalog',
    label: 'Catalog',
    items: [
      { id: 'nav-products', label: 'Products', icon: 'TagIcon', href: '/product-catalog' },
      { id: 'nav-suppliers', label: 'Suppliers', icon: 'TruckIcon', href: '/supplier-management' },
      { id: 'nav-categories', label: 'Categories', icon: 'Squares2X2Icon', href: '#' },
    ],
  },
  {
    id: 'group-crm',
    label: 'CRM',
    items: [
      { id: 'nav-customers', label: 'Customers', icon: 'BuildingOffice2Icon', href: '/crm' },
      { id: 'nav-customer-profiles', label: 'Customer Profiles', icon: 'IdentificationIcon', href: '/customer-profiles' },
      { id: 'nav-contacts', label: 'Contacts', icon: 'UsersIcon', href: '#' },
    ],
  },
  {
    id: 'group-finance',
    label: 'Finance',
    items: [
      { id: 'nav-invoices', label: 'Invoices', icon: 'DocumentDuplicateIcon', href: '/invoicing', badge: 8, badgeVariant: 'alert' },
      { id: 'nav-collections', label: 'Collections', icon: 'BanknotesIcon', href: '/collections', badge: 6, badgeVariant: 'alert' },
      { id: 'nav-commissions', label: 'Commissions', icon: 'ReceiptPercentIcon', href: '#' },
    ],
  },
  {
    id: 'group-system',
    label: 'System',
    items: [
      { id: 'nav-reports', label: 'Reports', icon: 'PresentationChartLineIcon', href: '/reports' },
      { id: 'nav-erply', label: 'ERPLY Sync', icon: 'ArrowPathIcon', href: '#', badge: 2, badgeVariant: 'warning' },
      { id: 'nav-settings', label: 'Settings', icon: 'Cog6ToothIcon', href: '/settings' },
    ],
  },
];

const badgeColors: Record<string, string> = {
  alert: 'bg-negative text-white',
  info: 'bg-info text-white',
  warning: 'bg-warning text-white',
};

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

export default function Sidebar({ collapsed, onToggle }: SidebarProps) {
  const pathname = usePathname();

  const isActive = (href: string) => {
    if (href === '/') return pathname === '/';
    return pathname === href;
  };

  return (
    <aside
      className={`
        fixed top-0 left-0 h-screen bg-card border-r border-border shadow-sidebar
        flex flex-col z-30 sidebar-transition scrollbar-thin overflow-y-auto overflow-x-hidden
        ${collapsed ? 'w-16' : 'w-60'}
      `}
    >
      {/* Logo area */}
      <div className={`flex items-center h-16 border-b border-border shrink-0 ${collapsed ? 'justify-center px-0' : 'px-4 gap-3'}`}>
        <AppLogo src="/assets/images/WhatsApp_Image_2026-07-31_at_07.00.48-1785516271424.jpeg" size={collapsed ? 36 : 40} className="rounded-md overflow-hidden" />
        {!collapsed && (
          <div className="flex flex-col min-w-0">
            <span className="text-sm font-700 text-foreground truncate leading-tight">Food Brokerage Services</span>
            <span className="text-2xs text-muted-foreground truncate">Antigua & Barbuda</span>
          </div>
        )}
      </div>

      {/* Nav groups */}
      <nav className="flex-1 py-3 space-y-1">
        {navGroups.map((group) => (
          <div key={group.id} className="mb-1">
            {!collapsed && (
              <p className="px-4 py-1.5 text-2xs font-600 uppercase tracking-widest text-muted-foreground">
                {group.label}
              </p>
            )}
            {group.items.map((item) => {
              const active = isActive(item.href);
              return (
                <Link
                  key={item.id}
                  href={item.href}
                  title={collapsed ? item.label : undefined}
                  className={`
                    flex items-center gap-3 mx-2 px-2 py-2 rounded-md text-sm font-500
                    transition-all duration-150 group relative
                    ${active
                      ? 'bg-secondary text-primary font-600' :'text-muted-foreground hover:bg-muted hover:text-foreground'
                    }
                    ${collapsed ? 'justify-center' : ''}
                  `}
                >
                  <Icon
                    name={item.icon as Parameters<typeof Icon>[0]['name']}
                    size={18}
                    variant={active ? 'solid' : 'outline'}
                    className={active ? 'text-primary' : 'text-muted-foreground group-hover:text-foreground'}
                  />
                  {!collapsed && (
                    <>
                      <span className="flex-1 truncate">{item.label}</span>
                      {item.badge !== undefined && (
                        <span className={`text-2xs font-700 px-1.5 py-0.5 rounded-full min-w-[20px] text-center ${badgeColors[item.badgeVariant ?? 'info']}`}>
                          {item.badge}
                        </span>
                      )}
                    </>
                  )}
                  {collapsed && item.badge !== undefined && (
                    <span className={`absolute top-0.5 right-0.5 w-2 h-2 rounded-full ${item.badgeVariant === 'alert' ? 'bg-negative' : item.badgeVariant === 'warning' ? 'bg-warning' : 'bg-info'}`} />
                  )}
                </Link>
              );
            })}
          </div>
        ))}
      </nav>

      {/* Bottom: collapse toggle + user */}
      <div className="border-t border-border p-2 space-y-1 shrink-0">
        {!collapsed && (
          <div className="flex items-center gap-2 px-2 py-2 rounded-md hover:bg-muted cursor-pointer transition-colors">
            <div className="w-7 h-7 rounded-full gradient-green flex items-center justify-center shrink-0">
              <span className="text-2xs font-700 text-white">MR</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-600 text-foreground truncate">Marcus Reid</p>
              <p className="text-2xs text-muted-foreground truncate">Owner</p>
            </div>
            <Icon name="ChevronUpDownIcon" size={14} className="text-muted-foreground shrink-0" />
          </div>
        )}
        <button
          onClick={onToggle}
          className="w-full flex items-center justify-center gap-2 px-2 py-2 rounded-md hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <Icon name={collapsed ? 'ChevronRightIcon' : 'ChevronLeftIcon'} size={16} />
          {!collapsed && <span className="text-xs font-500">Collapse</span>}
        </button>
      </div>
    </aside>
  );
}